import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    IconButton,
    Modal,
    Paper,
    Snackbar,
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    CircularProgress,
    TextField,
    InputAdornment,
    Tooltip
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import Table from "../../../components/Table/Table";
import { useGetTenancyBasicsQuery, useGetTenancyByIdQuery, useUpdateAutoSyncMutation, useDeleteTenancyGroupMutation } from "../../../store/services/tenancy.service";
import { useGetUsersFromGroupQuery, useLazyPreviewGroupDeletionQuery, useSetUsersToManualMutation } from "../../../store/services/user-service";
import ConfirmationDialog from "../../../utils/confirmationDialog";
import { downloadBlob, escapeCsv } from "../../reports-b/exportData";
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from "@mui/material/styles";
import GroupUsersTable from "../../users/GroupUsersTable";
import { SearchOutlined } from "@mui/icons-material";
import { getCardColor } from "../../../utils/colorUtils";
import SyncInactiveUserActionControl from "../../../components/SyncInactiveUserActionControl";


GroupTable.propTypes = {
    type: PropTypes.string.isRequired,
    groupIds: PropTypes.array,
    setGroupId: PropTypes.func,
    singleSelect: PropTypes.bool,
    hideFilterSwitch: PropTypes.bool
};

export default function GroupTable({ type, setGroupId, groupIds, singleSelect = false, hideFilterSwitch = false }) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [selected, setSelected] = useState([]);
    const [editGroups, setEditGroups] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [originalGroups, setOriginalGroups] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [confirmState, setConfirmState] = useState(null); // { group, willBeDeleted, safeOtherGroup }
    const [convertingToManual, setConvertingToManual] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });
    const [userPaginationModel, setUserPaginationModel] = useState({
        pageSize: 10,
        page: 0
    });

    const { data: tenancyData, error: tenancyError, isLoading: tenancyLoading, refetch } = useGetTenancyBasicsQuery({
        tenancyId: tenancyId
    });

    // Multi-Group Membership off means sync only ever keeps one UserGroupLink per user, so a user
    // genuinely in two provider groups shows here as belonging to whichever synced them last. The
    // other membership never reaches the DB until that group's own sync runs, so we can't tell a
    // "really has nowhere else to go" user from one who's quietly still in another group - hence
    // the blanket caveat below rather than a per-user flag.
    const { data: tenancy } = useGetTenancyByIdQuery({ tenancyId }, { skip: !tenancyId });

    const { data: groupData, error: groupError, isLoading: groupLoading } = useGetUsersFromGroupQuery({
        groupId: selectedGroupId,
        tenancyId: tenancyId
    }, {
        skip: !selectedGroupId
    });

    const [updateAutoSync] = useUpdateAutoSyncMutation();
    const [deleteTenancyGroup, { isLoading: isRemovingGroup }] = useDeleteTenancyGroupMutation();
    const [triggerPreviewGroupDeletion] = useLazyPreviewGroupDeletionQuery();
    const [setUsersToManual] = useSetUsersToManualMutation();

    const isGoogleSync = tenancyData?.groups?.some(group => group.origin === "google") ?? false;

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);

        // Perform the same action as "Edit Groups" then "Update Auto Sync"
        handleUpdateAutoSync().then(() => {
            setSnackbarSeverity("success");
            setSnackbarMessage("Refreshing users in the selected groups.");
            setSnackbarOpen(true);
        }).catch((error) => {
            console.error("Error refreshing groups:", error);
            setSnackbarSeverity("error");
            setSnackbarMessage("Failed to refresh groups.");
            setSnackbarOpen(true);
        }).finally(() => {
            setIsRefreshing(false);
        });
    }, [selectedGroups, tenancyId]); // Remove handleUpdateAutoSync from dependencies

    useEffect(() => {
        if (tenancyData && tenancyData.groups) {
            const initialSelectedGroups = tenancyData.groups.filter(group => group.autoSync);
            setSelectedGroups(initialSelectedGroups);
            setOriginalGroups(initialSelectedGroups);
        }
    }, [tenancyData]);

    useEffect(() => {
        if (!modalOpen) {
            setUserPaginationModel({
                pageSize: 10,
                page: 0
            });
        }
    }, [modalOpen]);

    const validateAutoSync = () => {
        if (selectedGroups.length === 0) return true;
        const origin = selectedGroups[0].origin;
        return selectedGroups.every(group => group.origin === origin);
    };

    const handleUpdateAutoSync = async () => {
        setIsRefreshing(true);
        // We are assuming that the first group selected is the type of sync (Google or Office).
        var origin = "office";
        if (selectedGroups.length > 0) {
            if (selectedGroups[0].origin === "google") {
                origin = "google";
            }
        }

        const updatedGroups = selectedGroups.map(group => ({
            ...group,
            DisplayName: group.name,  // Map 'name' field to 'DisplayName' for API compatibility
            autoSync: true,  // Explicitly set autoSync to true for selected groups
            CreateMe: true,
            origin: {
                type: origin
            },
            Id: group.office365Identifier
        }));

        const body = {
            TenancyId: tenancyId,
            Users: [],
            Groups: updatedGroups,
            AutoSync: true,
            Origin: { type: origin },
            Import: true
        };

        return updateAutoSync({ tenancyId, data: body })
            .unwrap()
            .then(() => {
                setIsRefreshing(false);
            })
            .catch((error) => {
                console.error(error);
                setIsRefreshing(false);
                throw error; // Re-throw to allow the caller to handle it
            });
    };


    const handleEditGroups = () => {
        if (editGroups) {
            handleUpdateAutoSync().then(() => {
                setSnackbarSeverity("success");
                setSnackbarMessage("Updated auto sync groups.");
                setSnackbarOpen(true);
            });
        } else {
            setOriginalGroups(selectedGroups);
        }
        setEditGroups(!editGroups);
    };

    const handleCancelEdit = () => {
        setSelectedGroups(originalGroups);
        setEditGroups(false);
    };

    const runRemoveGroup = async (group) => {
        try {
            await deleteTenancyGroup({ tenancyId, groupId: group.userGroupId }).unwrap();
            setSelectedGroups((prev) => prev.filter((g) => g.userGroupId !== group.userGroupId));
            setSnackbarSeverity("success");
            setSnackbarMessage(`Removed group "${group.name}".`);
            setSnackbarOpen(true);
            refetch();
        } catch (error) {
            setSnackbarSeverity("error");
            setSnackbarMessage(`Failed to remove "${group.name}".`);
            setSnackbarOpen(true);
        }
    };

    // BD-959: deleting a group removes its members from it immediately, and soft-deletes anyone
    // left with no other group. Preview that before deleting, so the admin sees it coming.
    const handleRemoveGroup = async (group) => {
        let preview;
        try {
            preview = await triggerPreviewGroupDeletion({ tenancyId, groupId: group.userGroupId }).unwrap();
        } catch (error) {
            console.error("Error previewing group deletion:", error);
            return runRemoveGroup(group); // don't block the delete on a failed preview
        }

        const willBeDeleted = preview?.willBeDeleted ?? [];
        const safeOtherGroup = preview?.safeOtherGroup ?? [];
        if (willBeDeleted.length === 0 && safeOtherGroup.length === 0) {
            return runRemoveGroup(group);
        }

        setConfirmState({ group, willBeDeleted, safeOtherGroup });
    };

    const handleDownloadDeletedUsersCsv = () => {
        if (!confirmState) return;
        const header = ["Email", "First Name", "Last Name"].join(",");
        const rows = confirmState.willBeDeleted.map((user) =>
            [user.email, user.firstName, user.lastName].map(escapeCsv).join(",")
        );
        downloadBlob([header, ...rows].join("\r\n"), "text/csv", `users-to-be-deleted-tenancy-${tenancyId}.csv`);
    };

    const handleMakeUsersManual = async () => {
        if (!confirmState) return;
        setConvertingToManual(true);
        try {
            const result = await setUsersToManual({
                tenancyId,
                userIds: confirmState.willBeDeleted.map((user) => user.id)
            }).unwrap();

            if (result?.data !== true) {
                throw new Error(result?.message || "Failed to convert users to manual");
            }

            const { group } = confirmState;
            setConfirmState(null);
            runRemoveGroup(group);
        } catch (error) {
            console.error("Error setting users to manual:", error);
            setSnackbarSeverity("error");
            setSnackbarMessage("Failed to make those users manual - nothing was changed, try again.");
            setSnackbarOpen(true);
        } finally {
            setConvertingToManual(false);
        }
    };

    const handleOpenModal = (groupId) => {
        setSelectedGroupId(groupId);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedGroupId(null);
        setUserPaginationModel({
            pageSize: 10,
            page: 0
        });
    };

    const getButtonBackgroundColor = (isSelected) => {
        if (isSelected) {
            return !editGroups ? theme.palette.primary.light : theme.palette.primary.main;
        }
        return !editGroups ? theme.palette.grey[100] : theme.palette.grey[200];
    };

    const getButtonColor = (isSelected) => {
        return isSelected && !editGroups ? 'white' : 'inherit';
    };

    const getButtonDisabledBackgroundColor = (isSelected) => {
        return isSelected ? theme.palette.primary.light : theme.palette.grey[100];
    };

    const getButtonDisabledColor = (isSelected) => {
        return isSelected ? 'black' : theme.palette.grey[400];
    };

    const headCells = [
        {
            id: "autoSync",
            label: "Auto Sync",
            render: (dataRow) => {
                const isSelected = selectedGroups.some(group => group.userGroupId === dataRow.userGroupId);
                return (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            if (editGroups) {
                                let newSelectedGroups;
                                if (isSelected) {
                                    newSelectedGroups = selectedGroups.filter(group => group.userGroupId !== dataRow.userGroupId);
                                } else {
                                    newSelectedGroups = [...selectedGroups, dataRow];
                                }
                                setSelectedGroups(newSelectedGroups);
                            }
                        }}
                        disabled={!editGroups}
                        color={isSelected ? "primary" : "inherit"}
                        sx={{
                            minWidth: 100,
                            '&.Mui-disabled': {
                                color: `${getButtonDisabledColor(isSelected)}!important`,
                            },
                            '&:hover': {
                                backgroundColor: (!isSelected && editGroups) ? lightMode ? theme.palette.grey[100] : theme.palette.black.light : undefined,
                            },
                        }}
                        /*
                        sx={{
                            minWidth: 100,
                            backgroundColor: getButtonBackgroundColor(isSelected),
                            color: getButtonColor(isSelected),
                            '&.Mui-disabled': {
                                backgroundColor: `${getButtonDisabledBackgroundColor(isSelected)}!important`,
                                color: `${getButtonDisabledColor(isSelected)}!important`,
                            },
                            '&:hover': {
                                backgroundColor: !isSelected && editGroups ? theme.palette.grey[50] : undefined,
                            },
                        }}
                        */

                    >
                        {isSelected ? "ON" : "OFF"}
                    </Button>
                );
            },
            initialVisible: type === "edit"
        },
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = groupIds?.map(group => group.userGroupId).includes(dataRow.userGroupId);
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (isSelected) {
                                setGroupId(groupIds.filter(group => group.userGroupId !== dataRow.userGroupId));
                            } else {
                                // If singleSelect mode, replace selection instead of adding
                                if (singleSelect) {
                                    setGroupId([dataRow]);
                                } else {
                                    setGroupId([...groupIds, dataRow]);
                                }
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon color={lightMode ? "black" : "white"} />}
                    </IconButton>
                );
            },
            initialVisible: type === "select"
        },
        {
            id: "viewUsers",
            label: "View users",
            render: (dataRow) => (
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleOpenModal(dataRow.userGroupId)}
                    color="warning"
                >
                    View users
                </Button>
            ),
            initialVisible: true
        },
        { id: "name", label: "Name", initialVisible: true },
        { id: "origin", label: "Origin", initialVisible: true },
        {
            id: "remove",
            label: "Remove",
            render: (dataRow) =>
                editGroups ? (
                    <IconButton
                        size="small"
                        color="error"
                        aria-label={`Remove ${dataRow.name}`}
                        onClick={() => handleRemoveGroup(dataRow)}
                        disabled={isRemovingGroup}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                ) : null,
            initialVisible: type === "edit"
        }

    ];

    const toolbarActions = [
        {
            render: () => (
                <Box>
                    {type === "edit" && (
                        <>
                            {selectedGroups.length > 0 && (
                                <Button
                                    size="medium"
                                    variant="contained"
                                    onClick={handleRefresh}
                                    sx={{ mr: 1 }}
                                    color='warning'
                                    disabled={editGroups || isRefreshing}
                                >
                                    {isRefreshing ? <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> : "Resync"}
                                </Button>
                            )}
                            <Button
                                size="medium"
                                variant="contained"
                                sx={{ textTransform: "capitalize", mr: 1 }}
                                aria-haspopup="true"
                                onClick={handleEditGroups}
                            >
                                {editGroups ? "Update Auto Sync" : "Edit Groups"}
                            </Button>
                            {editGroups && (
                                <Button
                                    size="medium"
                                    variant="outlined"
                                    sx={{ textTransform: "capitalize" }}
                                    aria-haspopup="true"
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            )
        }
    ];

    /* ------------------- OLD GROUP USERS COMPONENT -------------------

    const UserTable = () => {
        const [searchTerm, setSearchTerm] = useState('');

        const handleChangePage = (event, newPage) => {
            setUserPaginationModel(prev => ({ ...prev, page: newPage }));
        };

        const handleChangeRowsPerPage = (event) => {
            setUserPaginationModel({
                page: 0,
                pageSize: parseInt(event.target.value, 10)
            });
        };

        const filteredData = useMemo(() => {
            return (groupData || []).filter(user =>
                `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                new Date(user.dateCreated).toLocaleDateString("en-GB").includes(searchTerm)
            );
        }, [groupData, searchTerm]);

        if (tenancyLoading || groupLoading) {
            return (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Paper
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    display: 'grid',
                    gridTemplateRows: 'auto auto 1fr auto',
                    height: 550, // Increased height to accommodate search bar
                    boxShadow: 3,
                    borderRadius: 2
                }}
            >
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)'}}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search Users"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <TableContainer>
                    <MuiTable stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Date Created</TableCell>
                                <TableCell>Email</TableCell>
                            </TableRow>
                        </TableHead>
                    </MuiTable>
                </TableContainer>
                <TableContainer sx={{ overflowY: 'auto' }}>
                    <MuiTable>
                        <TableBody>
                            {filteredData
                                .slice(
                                    userPaginationModel.page * userPaginationModel.pageSize,
                                    userPaginationModel.page * userPaginationModel.pageSize + userPaginationModel.pageSize
                                )
                                .map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                        <TableCell>{new Date(user.dateCreated).toLocaleDateString("en-GB")}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </MuiTable>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={userPaginationModel.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={userPaginationModel.pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: '1px solid rgba(224, 224, 224, 1)',
                        '.MuiTablePagination-toolbar': {
                            height: '52px',
                        }
                    }}
                />
            </Paper>
        );
    };
    */

    return (
        <>
            <Table
                name={"group"}
                data={tenancyData && tenancyData.groups ? tenancyData.groups : []}
                error={tenancyError}
                loading={tenancyLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                showToolbarActions={true}
                toolbarActions={toolbarActions}
                onDelete={() => {}}
                selected={selected}
                setSelected={setSelected}
                maxTableHeight="calc(100vh - 400px)"
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                plural={"groups"}
                filterControl={!hideFilterSwitch ? (
                    <SyncInactiveUserActionControl tenancyId={tenancyId} isGoogle={isGoogleSync} />
                ) : null}
            />
            <ConfirmationDialog
                open={confirmState !== null}
                title="Delete this group?"
                content={confirmState && (
                    <>
                        Members who belong only to this group will be deleted with it, unless
                        they're in another group or converted to manual users first.
                    </>
                )}
                extraContent={confirmState && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {confirmState.willBeDeleted.length} user{confirmState.willBeDeleted.length === 1 ? "" : "s"} will be deleted
                            </Typography>
                            <Tooltip
                                title="This is a soft delete - the affected users can be recovered afterwards if needed."
                                placement="top"
                            >
                                <IconButton size="small">
                                    <HelpIcon fontSize="small" sx={{ color: "secondary.main" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography variant="body2" sx={{ mb: confirmState.willBeDeleted.length > 0 && !tenancy?.enableMultiGroup ? 1 : 2 }}>
                            {confirmState.safeOtherGroup.length} user{confirmState.safeOtherGroup.length === 1 ? "" : "s"} safe - won't be deleted
                        </Typography>

                        {confirmState.willBeDeleted.length > 0 && !tenancy?.enableMultiGroup && (
                            <Typography variant="body2" sx={{ mb: 2, fontStyle: "italic" }}>
                                Multi-Group Membership is off for this tenancy, so some of these users may reappear
                                automatically on their next sync if they belong to another group in Microsoft Entra
                                or Google Workspace that isn't reflected here.
                            </Typography>
                        )}

                        {confirmState.willBeDeleted.length > 0 && confirmState.willBeDeleted.length < 50 && (
                            <Box sx={{
                                maxHeight: 200,
                                overflowY: "auto",
                                border: "1px solid rgba(224, 224, 224, 1)",
                                borderRadius: 1,
                                p: 1,
                                mb: 2
                            }}>
                                {confirmState.willBeDeleted.map((user) => (
                                    <Typography key={user.id} variant="body2">
                                        {user.firstName} {user.lastName} ({user.email})
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {confirmState.willBeDeleted.length > 0 && (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Button size="small" variant="outlined" onClick={handleDownloadDeletedUsersCsv}>
                                    Download CSV
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={handleMakeUsersManual}
                                    disabled={convertingToManual}
                                >
                                    {convertingToManual
                                        ? <CircularProgress size={16} color="inherit" />
                                        : "Make these users manual instead"}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
                confirmDisabled={convertingToManual}
                onCancel={() => setConfirmState(null)}
                onConfirm={() => {
                    const { group } = confirmState;
                    setConfirmState(null);
                    runRemoveGroup(group);
                }}
            />
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 900,
                    bgcolor: theme.palette.background.default,
                    boxShadow: 24,
                    borderRadius: 2,
                    p: 4,

                }}>
                    <Typography id="modal-modal-title" variant="h4" component="h2" gutterBottom>
                        Group Users
                    </Typography>
                    <GroupUsersTable
                        selectedGroupId={selectedGroupId}
                        closeModal={handleCloseModal}
                    />
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt:2 }}>
                        <Button onClick={handleCloseModal} variant="outlined" color="black">Close</Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}
